<template>
    <div v-if="isRelative" class="grid-content">
        <div class="grid-item">
            <v-relative-date-input
                :value="start"
                @input="updateStart"
                :placeholder="placeholder"
                left-label="Start"/>
        </div>
        <div class="grid-item">
            <v-relative-date-input
                :value="end"
                @input="updateEnd"
                :placeholder="placeholder"
                left-label="End"/>
        </div>
    </div>
    <div v-else-if="isEmpty"><v-date-input :disabled="true"/></div>
    <div v-else class="grid-fitted">
        <div class="grid-item grid-item-half">
            <v-date-input
                class="inline-xs"
                :type="dateInputType"
                :value="min"
                :validate="validate"
                @input="updateMin"
            />
        </div>
        <div class="grid-item grid-item-half">
            <v-date-input
                :type="dateInputType"
                :value="max"
                :validate="validate"
                @input="updateMax"
            />
        </div>
    </div>
</template>

<script>
    import {vRelativeDateInput, vDateInput} from '@/components/form';
    import {DateTimeFormats} from '@/common/datetime-formats';
    import BaseEditor from '@/components/query2/components/editors/base-editor';
    import QueryLine from '@/components/query2/query-line';

    export default {
        name: 'datetime-editor',
        extends: BaseEditor,
        components: {vRelativeDateInput, vDateInput},
        computed: {
            isRelative() {
                return this.item.isRelativeDatetime;
            },
            isEmpty() {
                return this.item.isEmpty || this.item.isNotEmpty;
            },
            dateInputType() {
                return this.item.isCustomField && this.item.config.type === QueryLine.CustomFieldTypes.date
                    ? QueryLine.CustomFieldTypes.date
                    : QueryLine.Types.datetime;
            },
        },
        watch: {
            'item.operator': function update(operator) {
                if (operator === QueryLine.Operators.isEmpty
                    || operator === QueryLine.Operators.isNotEmpty) {
                    this.dispatchUpdate([]);
                }
            },
        },
        data() {
            const {isRelativeDatetime} = this.item;
            const values = this.item.getValues();
            let start = null;
            let end = null;
            let min = null;
            let max = null;
            if (values) {
                if (isRelativeDatetime) {
                    [start, end] = values;
                } else {
                    [min, max] = this.decodeDatetime(values);
                }
            }
            return {
                start,
                end,
                min,
                max,
                placeholder: 'Enter a value',
            };
        },
        methods: {
            /**
             * Update the relative start date.
             * @param {string} start
             */
            updateStart(start) {
                this.start = start;
                this.dispatchUpdate();
            },
            /**
             * Update the relative end date.
             * @param {string} end
             */
            updateEnd(end) {
                this.end = end;
                this.dispatchUpdate();
            },
            /**
             * Update the absolute start date.
             * @param {Moment} min
             */
            updateMin(min) {
                this.min = min;
                this.dispatchUpdate();
            },
            /**
             * Update the absolute end date.
             * @param {Moment} max
             */
            updateMax(max) {
                // TODO set to end of day by default?
                this.max = max;
                this.dispatchUpdate();
            },
            encodeData() {
                // when using relative dates we don't need any formatting
                if (this.isRelative) {
                    return [this.start, this.end];
                }
                // otherwise it's either date or datetime
                let format;
                if (this.dateInputType === QueryLine.CustomFieldTypes.date) {
                    // format without the time for the `date` custom fields
                    format = DateTimeFormats.datePickerDate;
                }
                return [
                    this.min && this.min.isValid() ? this.min.format(format) : null,
                    this.max && this.max.isValid() ? this.max.format(format) : null,
                ];
            },
            decodeDatetime([rawMin = null, rawMax = null]) {
                // TODO handle custom field of date-only format
                return [
                    rawMin ? this.$tz()
                        .readUTC(rawMin) : null,
                    rawMax ? this.$tz()
                        .readUTC(rawMax) : null,
                ];
            },
        },
    };
</script>
